/**
 * NotificationCenter Component (MA-OPS-109)
 * 
 * Bell icon with dropdown showing recent notifications.
 * Handles quarantine notifications with click-through navigation.
 * 
 * Features:
 * - Unread badge count
 * - Auto-refresh polling
 * - Mark as read on click
 * - Mark all read
 * - Severity-based styling (warning/critical)
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Bell, AlertTriangle, Shield, ExternalLink, Check, CheckCheck, Trophy } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";

// Polling interval for notification updates (30 seconds)
const POLL_INTERVAL = 30000;

export default function NotificationCenter() {
  const navigate = useNavigate();
  const { hasModule } = usePermissions();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await api.get("/notifications", {
        params: { limit: 20 },
      });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
      setError(null);
    } catch (err) {
      console.error("[NotificationCenter] Failed to fetch notifications:", err);
      setError("Failed to load notifications");
    }
  }, []);

  // Fetch unread count (lightweight polling)
  const fetchUnreadCount = useCallback(async () => {
    // MA-PERMISSION-INTEGRITY-AUDIT-001: Use canonical "settings" module
    if (typeof hasModule !== 'function' || !hasModule("settings")) {
      return;
    }
    
    try {
      const response = await api.get("/notifications/unread-count");
      setUnreadCount(response.data.unread_count || 0);
    } catch (err) {
      // Silent fail for polling - don't spam console
      if (err.response?.status !== 403) {
        console.debug("[NotificationCenter] Polling failed:", err);
      }
    }
  }, [hasModule]);

  // Initial fetch and polling
  useEffect(() => {
    fetchUnreadCount();
    
    // Set up polling
    const pollInterval = setInterval(fetchUnreadCount, POLL_INTERVAL);
    
    return () => clearInterval(pollInterval);
  }, [fetchUnreadCount]);

  // Fetch full list when dropdown opens
  useEffect(() => {
    if (open) {
      setLoading(true);
      fetchNotifications().finally(() => setLoading(false));
    }
  }, [open, fetchNotifications]);

  // Mark single notification as read
  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, is_read: true } : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("[NotificationCenter] Failed to mark as read:", err);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, is_read: true }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error("[NotificationCenter] Failed to mark all as read:", err);
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    
    // Navigate to action URL
    if (notification.action_url) {
      setOpen(false);
      navigate(notification.action_url);
    }
  };

  // Render signal strength badge for experiment notifications
  const renderSignalBadge = (notification) => {
    if (notification.type !== "experiment_winner") return null;
    const sig = notification.metadata?.signal_strength;
    const label = notification.metadata?.signal_label;
    if (!sig || !label) return null;
    const colors = {
      strong: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
      building: "bg-amber-500/20 text-amber-300 border-amber-500/30",
      low: "bg-red-500/20 text-red-300 border-red-500/30",
    };
    return (
      <span className={cn("inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded border", colors[sig] || colors.low)} data-testid="signal-strength-badge">
        {label}
      </span>
    );
  };

  // Get icon for notification type
  const getNotificationIcon = (notification) => {
    const { type, severity } = notification;
    if (type === "experiment_winner") {
      const sig = notification.metadata?.signal_strength;
      const sigColor = sig === "strong" ? "text-emerald-400" : sig === "building" ? "text-amber-400" : "text-red-400";
      return <Trophy className={cn("h-5 w-5", sigColor)} />;
    }
    if (type === "quarantine_spike") {
      return (
        <AlertTriangle
          className={cn(
            "h-5 w-5",
            severity === "critical" ? "text-red-400" : "text-amber-400"
          )}
        />
      );
    }
    return (
      <Shield
        className={cn(
          "h-5 w-5",
          severity === "critical" ? "text-red-400" : "text-amber-400"
        )}
      />
    );
  };

  // Get background color based on type, severity and read state
  const getNotificationBg = (notification) => {
    if (notification.is_read) {
      return "bg-slate-800/30";
    }
    if (notification.type === "experiment_winner") {
      return "bg-emerald-950/30 border-l-2 border-emerald-500";
    }
    if (notification.severity === "critical") {
      return "bg-red-950/40 border-l-2 border-red-500";
    }
    if (notification.severity === "warning") {
      return "bg-amber-950/30 border-l-2 border-amber-500";
    }
    return "bg-slate-800/50";
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          className="relative h-10 w-10 rounded-full bg-white/10 p-0 text-slate-100 ring-1 ring-white/10 hover:bg-white/15"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              className="absolute -right-1 -top-1 h-5 min-w-5 flex items-center justify-center rounded-full bg-red-500 text-[10px] font-semibold text-white px-1"
              data-testid="notification-badge"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent
        align="end"
        className="w-96 p-0 bg-slate-950 text-slate-100 ring-1 ring-white/10"
        data-testid="notification-dropdown"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-slate-400" />
            <span className="font-medium">Notifications</span>
            {unreadCount > 0 && (
              <Badge variant="secondary" className="bg-indigo-500/20 text-indigo-200 text-xs">
                {unreadCount} new
              </Badge>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto px-2 py-1 text-xs text-slate-400 hover:text-slate-100"
              onClick={markAllAsRead}
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>

        {/* Notification List */}
        <ScrollArea className="max-h-[400px]" data-testid="notification-list">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin h-6 w-6 border-2 border-slate-500 border-t-indigo-400 rounded-full" />
            </div>
          ) : error ? (
            <div className="px-4 py-8 text-center text-sm text-red-400">
              {error}
            </div>
          ) : notifications.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-500">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
              No notifications yet
            </div>
          ) : (
            <div className="py-1">
              {notifications.map((notification) => (
                <button
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={cn(
                    "w-full text-left px-4 py-3 hover:bg-white/5 transition-colors cursor-pointer",
                    getNotificationBg(notification)
                  )}
                  data-testid={`notification-item-${notification.id}`}
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getNotificationIcon(notification)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p
                          className={cn(
                            "text-sm font-medium",
                            notification.is_read ? "text-slate-400" : "text-slate-100"
                          )}
                        >
                          {notification.title}
                        </p>
                        {!notification.is_read && (
                          <span className="flex-shrink-0 h-2 w-2 rounded-full bg-indigo-400 mt-1.5" />
                        )}
                      </div>
                      <p
                        className={cn(
                          "text-xs mt-1 line-clamp-2 whitespace-pre-line",
                          notification.is_read ? "text-slate-500" : "text-slate-300"
                        )}
                      >
                        {notification.message}
                      </p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {renderSignalBadge(notification)}
                        <span className="text-[10px] text-slate-500">
                          {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                        </span>
                        {notification.action_url && (
                          <span className="text-[10px] text-indigo-400 flex items-center gap-0.5">
                            <ExternalLink className="h-2.5 w-2.5" />
                            {notification.action_label || "View"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        {notifications.length > 0 && (
          <>
            <Separator className="bg-white/10" />
            <div className="px-4 py-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs text-slate-400 hover:text-slate-100"
                onClick={() => {
                  setOpen(false);
                  navigate("/admin/diagnostics?filter=quarantine");
                }}
                data-testid="view-all-notifications-btn"
              >
                View Quarantine Queue
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
